import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faStop,
  faWandMagicSparkles,
} from "@fortawesome/pro-solid-svg-icons";
import { useAuth } from "react-oidc-context";
import Tooltip from "./Tooltip";
import Badge from "./Badge";
import AlertPopup from "./AlertPopup";
import useSpeechToText from "../../hooks/useSpeechToText";
import AiSpeechToTextApi from "../../api/aiSpeechToTextApi";
import { cleanSTTText } from "../../helpers/speechToTextClean";
import {
  formatCountdownMmSs,
  LIMIT_INFO_DISMISS_MS,
  MAX_SESSION_MS,
  MAX_SUMMARISE_UI_CHARS,
  MIN_SUMMARISE_CHARS,
  PRE_TIMEOUT_WARNING_MS,
  SESSION_STORAGE_LIMIT_INFO_KEY,
} from "../../helpers/speechToTextLimits";
import { StaggerTextReveal } from "../transitions";

interface DictationSegment {
  startPos: number;
  text: string;
}

interface SummariseUndoState {
  startPos: number;
  beforeText: string;
  summaryLength: number;
  summaryText: string;
}

const DEFAULT_ACTION_LINK_CLASSNAME =
  "text-sm text-gray-700 underline cursor-pointer hover:text-gray-900";

const hoverOnlyUnderlineClassName = (className: string) =>
  className.replace(/\bunderline\b/g, "no-underline hover:underline");

const DEFAULT_STATUS_TEXT_CLASSNAME = "text-xs text-gray-600";

const DEFAULT_IDLE_MIC_CLASSNAME =
  "text-gray-400 hover:text-gray-500 focus:outline-none";

const LIMIT_INFO_LINES = [
  { text: "Just so you know...", emphasis: true },
  { text: "recording is limited to 10 minutes at a time" },
] as const;

interface SpeechToTextVoiceRowProps {
  value: string;
  onChange: (newValue: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  cursorPositionRef: React.MutableRefObject<number>;
  maxLength?: number;
  onCommit?: (latestValue: string) => void;
  actionLinkClassName?: string;
  statusTextClassName?: string;
  idleMicClassName?: string;
  errorAsBadge?: boolean;
  errorBadgeBackgroundColourClassName?: string;
  errorBadgeTextColourClassName?: string;
  errorBadgeBorderRadiusClassName?: string;
}

const SpeechToTextVoiceRow = ({
  value,
  onChange,
  textareaRef,
  cursorPositionRef,
  maxLength,
  onCommit,
  actionLinkClassName = DEFAULT_ACTION_LINK_CLASSNAME,
  statusTextClassName = DEFAULT_STATUS_TEXT_CLASSNAME,
  idleMicClassName = DEFAULT_IDLE_MIC_CLASSNAME,
  errorAsBadge = false,
  errorBadgeBackgroundColourClassName = "bg-white",
  errorBadgeTextColourClassName = "text-red-700",
  errorBadgeBorderRadiusClassName = "rounded-full",
}: SpeechToTextVoiceRowProps) => {
  const auth = useAuth();
  const api = useMemo(
    () => new AiSpeechToTextApi(auth.user?.access_token),
    [auth.user?.access_token],
  );

  const wasRecordingRef = useRef<boolean>(false);
  const recordingStartCursorRef = useRef<number>(0);
  const insertedTextLengthRef = useRef<number>(0);
  const lastProcessedInterimResultRef = useRef<string>("");
  const valueRef = useRef<string>(value || "");
  const recordingStartedAtRef = useRef<number | null>(null);
  const maxSessionTimerRef = useRef<number | null>(null);
  const lastSuccessfulSummariseRef = useRef<{
    inputKey: string;
    summary: string;
  } | null>(null);

  const [elapsedMs, setElapsedMs] = useState(0);
  const [showLimitInfo, setShowLimitInfo] = useState(false);
  const [lastDictation, setLastDictation] = useState<DictationSegment | null>(
    null,
  );
  const [summariseUndo, setSummariseUndo] = useState<SummariseUndoState | null>(
    null,
  );
  const [isSummarising, setIsSummarising] = useState(false);
  const [summariseError, setSummariseError] = useState<string | null>(null);
  const [sessionEndedMessage, setSessionEndedMessage] = useState<string | null>(
    null,
  );
  const [showSummariseTooLongModal, setShowSummariseTooLongModal] =
    useState(false);

  const getRealtimeToken = useCallback(() => api.getRealtimeToken(), [api]);

  const {
    error,
    interimResult,
    isRecording,
    startSpeechToText,
    stopSpeechToText,
  } = useSpeechToText({
    language: "en",
    enablePartials: true,
    maxDelay: 1,
    getRealtimeToken,
  });

  const errorMessage = error || summariseError;

  useEffect(() => {
    valueRef.current = value || "";
  }, [value]);

  useEffect(() => {
    const current = value || "";

    if (!current.trim()) {
      lastSuccessfulSummariseRef.current = null;
      setLastDictation(null);
      setSummariseUndo(null);
      return;
    }

    setLastDictation((prev) => {
      if (!prev) return prev;
      const segment = current.slice(
        prev.startPos,
        prev.startPos + prev.text.length,
      );
      if (segment !== prev.text) {
        lastSuccessfulSummariseRef.current = null;
        return null;
      }
      return prev;
    });

    setSummariseUndo((prev) => {
      if (!prev) return prev;
      const segment = current.slice(
        prev.startPos,
        prev.startPos + prev.summaryLength,
      );
      return segment === prev.summaryText ? prev : null;
    });
  }, [value]);

  const commitValue = useCallback(
    (latestValue: string) => {
      if (!onCommit) return;
      window.setTimeout(() => onCommit(latestValue), 0);
    },
    [onCommit],
  );

  const updateCursorPosition = () => {
    if (textareaRef.current) {
      cursorPositionRef.current = textareaRef.current.selectionStart || 0;
    }
  };

  const resolveRecordingStartPosition = () => {
    const textarea = textareaRef.current;
    const currentValue = valueRef.current;
    const isTextareaFocused =
      textarea !== null && document.activeElement === textarea;

    if (isTextareaFocused) {
      updateCursorPosition();
      return;
    }

    const endPos = currentValue.length;
    cursorPositionRef.current = endPos;
    if (textarea) {
      textarea.selectionStart = endPos;
      textarea.selectionEnd = endPos;
    }
  };

  const clearSessionTimers = () => {
    if (maxSessionTimerRef.current !== null) {
      window.clearTimeout(maxSessionTimerRef.current);
      maxSessionTimerRef.current = null;
    }
    recordingStartedAtRef.current = null;
    setElapsedMs(0);
  };

  useEffect(() => {
    // Clear session timers on component unmount.
    // If the textarea unmounts while a recording is active, the timeout callback will still
    // fire and call setSessionEndedMessage, causing a state update on an unmounted component.
    return () => {
      if (maxSessionTimerRef.current !== null) {
        window.clearTimeout(maxSessionTimerRef.current);
        maxSessionTimerRef.current = null;
      }
    };
  }, []);

  const handleStopRecording = useCallback(async () => {
    await stopSpeechToText();
    clearSessionTimers();
  }, [stopSpeechToText]);

  const replaceSegment = (
    startPos: number,
    oldLength: number,
    newText: string,
  ): number => {
    const currentValue = valueRef.current;
    const beforeText = currentValue.slice(0, startPos);
    const afterText = currentValue.slice(startPos + oldLength);
    let segmentText = newText;

    if (maxLength !== undefined) {
      const maxSegmentLength = maxLength - beforeText.length - afterText.length;
      if (maxSegmentLength <= 0) {
        segmentText = "";
      } else if (segmentText.length > maxSegmentLength) {
        segmentText = segmentText.slice(0, maxSegmentLength);
      }
    }

    let newValue = beforeText + segmentText + afterText;
    if (maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    valueRef.current = newValue;
    onChange(newValue);

    window.setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = startPos + segmentText.length;
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
      }
    }, 0);

    return segmentText.length;
  };

  const applySummariseResult = useCallback(
    (startPos: number, beforeText: string, summary: string) => {
      const summaryLength = replaceSegment(
        startPos,
        beforeText.length,
        summary,
      );
      setSummariseUndo({
        startPos,
        beforeText,
        summaryLength,
        summaryText: summary,
      });
      setLastDictation(null);
      commitValue(valueRef.current);
    },
    [commitValue],
  );

  useEffect(() => {
    if (
      interimResult !== lastProcessedInterimResultRef.current &&
      interimResult &&
      isRecording
    ) {
      const currentValue = valueRef.current;
      const startPos = recordingStartCursorRef.current;
      const beforeText = currentValue.slice(0, startPos);
      const afterText = currentValue.slice(
        startPos + insertedTextLengthRef.current,
      );
      let newValue = beforeText + interimResult + afterText;

      if (maxLength !== undefined && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength);
        void handleStopRecording();
      }

      valueRef.current = newValue;
      lastProcessedInterimResultRef.current = interimResult;
      insertedTextLengthRef.current = Math.min(
        interimResult.length,
        newValue.length - startPos,
      );

      onChange(newValue);

      window.setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = startPos + insertedTextLengthRef.current;
          textareaRef.current.selectionStart = newCursorPos;
          textareaRef.current.selectionEnd = newCursorPos;
        }
      }, 0);
    }
  }, [
    interimResult,
    isRecording,
    onChange,
    maxLength,
    handleStopRecording,
    textareaRef,
  ]);

  useEffect(() => {
    if (isRecording && !wasRecordingRef.current) {
      recordingStartCursorRef.current = cursorPositionRef.current;
      insertedTextLengthRef.current = 0;
      lastProcessedInterimResultRef.current = "";
      recordingStartedAtRef.current = Date.now();
      setSessionEndedMessage(null);
      setSummariseError(null);

      maxSessionTimerRef.current = window.setTimeout(() => {
        void handleStopRecording();
        setSessionEndedMessage("Recording stopped - 10 minute limit reached.");
      }, MAX_SESSION_MS);
    } else if (!isRecording && wasRecordingRef.current) {
      const currentValue = valueRef.current;
      const startPos = recordingStartCursorRef.current;
      const insertedLength = insertedTextLengthRef.current;

      if (insertedLength > 0) {
        const insertedText = currentValue.slice(
          startPos,
          startPos + insertedLength,
        );
        const cleanedText = cleanSTTText(insertedText);
        replaceSegment(startPos, insertedLength, cleanedText);
        setLastDictation({ startPos, text: cleanedText });
        setSummariseUndo(null);
      }

      insertedTextLengthRef.current = 0;
      lastProcessedInterimResultRef.current = "";
      clearSessionTimers();
      commitValue(valueRef.current);

      if (!sessionStorage.getItem(SESSION_STORAGE_LIMIT_INFO_KEY)) {
        setShowLimitInfo(true);
        sessionStorage.setItem(SESSION_STORAGE_LIMIT_INFO_KEY, "1");
      }
    }

    wasRecordingRef.current = isRecording;
  }, [isRecording, handleStopRecording, textareaRef, commitValue]);

  useEffect(() => {
    if (!isRecording || recordingStartedAtRef.current === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (recordingStartedAtRef.current === null) return;
      setElapsedMs(Date.now() - recordingStartedAtRef.current);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isRecording]);

  const remainingSessionMs = MAX_SESSION_MS - elapsedMs;
  const showPreTimeoutWarning =
    isRecording && remainingSessionMs <= PRE_TIMEOUT_WARNING_MS;

  const handleVoiceRecordToggle = () => {
    if (isRecording) {
      void handleStopRecording();
      return;
    }

    resolveRecordingStartPosition();
    void startSpeechToText();
  };

  const handleSummarise = async () => {
    if (!lastDictation || isSummarising) return;

    const fullText = valueRef.current || "";
    const trimmedText = fullText.trim();
    if (trimmedText.length < MIN_SUMMARISE_CHARS) return;

    if (trimmedText.length > MAX_SUMMARISE_UI_CHARS) {
      setShowSummariseTooLongModal(true);
      return;
    }

    const inputKey = trimmedText;
    const cached = lastSuccessfulSummariseRef.current;

    if (cached?.inputKey === inputKey) {
      setSummariseError(null);
      applySummariseResult(0, fullText, cached.summary);
      return;
    }

    setIsSummarising(true);
    setSummariseError(null);

    const result = await api.summarize(trimmedText);

    setIsSummarising(false);

    if (result === "RATE_LIMITED") {
      setSummariseError(
        "You have reached the maximum number of summarise requests per hour.",
      );
      return;
    }

    if (!result?.summary) {
      setSummariseError("Could not summarise. Please try again.");
      return;
    }

    lastSuccessfulSummariseRef.current = { inputKey, summary: result.summary };
    applySummariseResult(0, fullText, result.summary);
  };

  const handleUndoSummarise = () => {
    if (!summariseUndo) return;
    replaceSegment(
      summariseUndo.startPos,
      summariseUndo.summaryLength,
      summariseUndo.beforeText,
    );
    setLastDictation({
      startPos: summariseUndo.startPos,
      text: summariseUndo.beforeText,
    });
    setSummariseUndo(null);
    commitValue(valueRef.current);
  };

  const showSummariseAction =
    !!lastDictation &&
    (value || "").trim().length >= MIN_SUMMARISE_CHARS &&
    !isRecording;

  const forceExpanded =
    isRecording ||
    isSummarising ||
    (showLimitInfo && !isRecording) ||
    showPreTimeoutWarning ||
    !!sessionEndedMessage ||
    !!error ||
    !!summariseError ||
    showSummariseAction ||
    (!!summariseUndo && !isRecording);

  const idleMicButton = !isRecording ? (
    <Tooltip
      content="Start voice recording"
      appearAt="top"
      triggerElement={
        <button
          type="button"
          onClick={handleVoiceRecordToggle}
          className={cx(
            "flex items-center justify-center h-8 w-8",
            "bg-transparent border-0 cursor-pointer",
            idleMicClassName,
          )}
          aria-label="Start voice recording"
        >
          <FontAwesomeIcon icon={faMicrophone} className="w-4 h-4" />
        </button>
      }
    />
  ) : null;

  return (
    <>
      <div
        className={cx(
          "stt-voice-row pointer-events-none print:hidden",
          forceExpanded && "stt-force-expanded",
        )}
      >
        {!isRecording && (
          <div className="stt-tier1 pointer-events-auto">{idleMicButton}</div>
        )}

        <div className="stt-tier2">
          <div className="flex-1 min-w-0 text-left">
            <StaggerTextReveal
              visible={showLimitInfo && !isRecording}
              lines={[...LIMIT_INFO_LINES]}
              className={statusTextClassName}
              autoDismissMs={LIMIT_INFO_DISMISS_MS}
              onHidden={() => setShowLimitInfo(false)}
            />

            {showPreTimeoutWarning && (
              <p
                className="text-xs font-medium text-amber-700"
                role="status"
                aria-live="polite"
              >
                Recording will stop in{" "}
                <span className="font-mono tabular-nums">
                  {formatCountdownMmSs(remainingSessionMs)}
                </span>
              </p>
            )}

            {sessionEndedMessage && (
              <p className={statusTextClassName}>{sessionEndedMessage}</p>
            )}

            {errorMessage &&
              (errorAsBadge ? (
                <div role="alert">
                  <Badge
                    text={errorMessage}
                    backgroundColourClassName={
                      errorBadgeBackgroundColourClassName
                    }
                    textColourClassName={errorBadgeTextColourClassName}
                    borderRadiusClassName={errorBadgeBorderRadiusClassName}
                    marginClassName="mx-0"
                    classNames="shadow-sm whitespace-normal"
                  />
                </div>
              ) : (
                <p className="text-xs text-red-600" role="alert">
                  {errorMessage}
                </p>
              ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap shrink-0 justify-end">
            {showSummariseAction && (
              <button
                type="button"
                onClick={() => void handleSummarise()}
                disabled={isSummarising}
                className={cx(
                  isSummarising
                    ? hoverOnlyUnderlineClassName(actionLinkClassName)
                    : actionLinkClassName,
                  "inline-flex items-center gap-1",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "bg-transparent border-0 p-0 focus:outline-none",
                )}
              >
                <FontAwesomeIcon
                  icon={faWandMagicSparkles}
                  className="w-3 h-3 shrink-0"
                  aria-hidden
                />
                {isSummarising ? "Summarising..." : "Summarise"}
              </button>
            )}

            {summariseUndo && !isRecording && (
              <button
                type="button"
                onClick={handleUndoSummarise}
                className={cx(
                  hoverOnlyUnderlineClassName(actionLinkClassName),
                  "bg-transparent border-0 p-0 focus:outline-none",
                )}
              >
                Undo summarise
              </button>
            )}

            {isRecording && (
              <Tooltip
                content="Stop recording"
                appearAt="top"
                triggerElement={
                  <button
                    type="button"
                    onClick={() => void handleStopRecording()}
                    className={cx(
                      "flex items-center justify-center p-1",
                      "text-red-500 hover:text-red-600 cursor-pointer",
                      "bg-transparent border-0 focus:outline-none",
                    )}
                    aria-label="Stop recording"
                  >
                    <FontAwesomeIcon icon={faStop} className="w-4 h-4" />
                  </button>
                }
              />
            )}

            {isRecording ? (
              <span
                className="flex items-center justify-center h-9 w-9"
                role="status"
                aria-label="Recording in progress"
              >
                <FontAwesomeIcon
                  icon={faMicrophone}
                  className="w-4 h-4 text-green-500 stt-recording-pulse"
                />
              </span>
            ) : (
              <Tooltip
                content="Start voice recording"
                appearAt="top"
                triggerElement={
                  <button
                    type="button"
                    onClick={handleVoiceRecordToggle}
                    className={cx(
                      "flex items-center justify-center h-9 w-9 rounded-full",
                      "bg-white border border-gray-200 shadow-sm",
                      "transition-colors duration-200",
                      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300",
                      "hover:bg-gray-50",
                    )}
                    aria-label="Start voice recording"
                  >
                    <FontAwesomeIcon
                      icon={faMicrophone}
                      className="w-4 h-4 text-gray-500 hover:text-gray-600"
                    />
                  </button>
                }
              />
            )}
          </div>
        </div>
      </div>

      <AlertPopup
        compact
        isOpen={showSummariseTooLongModal}
        onOpenChange={setShowSummariseTooLongModal}
        onPrimaryButtonClick={() => setShowSummariseTooLongModal(false)}
        primaryButtonText="OK"
        title="Too long to summarise"
        bodyText={`This dictation is over ${MAX_SUMMARISE_UI_CHARS} characters. Shorten it or edit the text before summarising.`}
      />
    </>
  );
};

export default SpeechToTextVoiceRow;
